// Shared helpers for attaching cattle weigh-in entries to a processing batch.
// Called from both the webform (WeighInsWebform) and the admin tab
// (CattleWeighInsView) so the two surfaces land identical DB state when a
// cattle finisher session is completed with send-to-processor entries.

import type {RpcEnvelope, SupabaseClient} from './supabaseTypes.js';

// Row shapes below describe ONLY the columns these helpers read or write. Every
// other column of the real table stays out of the type: `select('*')` hands over
// whole rows and the client has no generated database types to check them
// against, so an index signature carries the rest as `unknown`.

// One entry of the batch's cows_detail jsonb array.
export type CowDetailRow = {
  cattle_id?: string | null;
  tag?: string | null;
  live_weight?: number | string | null;
  hanging_weight?: number | string | null;
};

// A prior tag carried on cattle.old_tags. `source` distinguishes WCF's own
// renumbering from an imported selling-farm tag, which the matcher excludes.
export type CattleOldTag = {tag?: string | null; source?: string | null};

export type CattleRow = {
  id: string;
  tag?: string | null;
  herd?: string | null;
  old_tags?: CattleOldTag[] | null;
  [column: string]: unknown;
};

// A weigh-in entry as the attach path reads it.
export type WeighInEntryRow = {id: string; tag?: string | null; weight?: number | string | null};

export type CattleProcessingBatchRow = {
  id: string;
  name?: string | null;
  status?: string | null;
  cows_detail?: CowDetailRow[] | null;
  [column: string]: unknown;
};

export type AttachSkipReason = 'no_cow_for_tag' | 'already_in_batch' | 'tagless';

export type CattleAttachResult = {
  attached: {entry: WeighInEntryRow; cow: CattleRow}[];
  skipped: {entry: WeighInEntryRow; reason: AttachSkipReason}[];
};

// Completing the session is a separate request from attaching cattle. A
// completion-only retry must not replay a confirmed attach, while newly flagged
// entries and another session still need their own send.
export function pendingCattleProcessorEntries<T extends {id?: string; send_to_processor?: unknown}>(
  sessionId: string,
  entries: T[],
  confirmedBySession: ReadonlyMap<string, ReadonlySet<string>>,
): T[] {
  const confirmed = confirmedBySession.get(sessionId);
  return entries.filter((entry) => entry.send_to_processor === true && (!entry.id || !confirmed?.has(entry.id)));
}

export class CattleProcessingAttachError extends Error {
  constructor(
    message: string,
    readonly batchId: string,
    readonly listedCount: number,
    readonly movedCount: number,
    readonly transfersRecorded: number,
    readonly transfersExpected: number,
    readonly failedTag: string | null,
  ) {
    super(message);
    this.name = 'CattleProcessingAttachError';
  }
}

// One planned change in a reconcile plan. `expectedName` is the stale-plan
// guard the migration re-checks server-side; `targetName` applies to renames.
export type ScheduledBatchPlanRow = {id: string; expectedName?: string | null; targetName?: string | null};

// parseFloat's declared parameter is `string`, but these helpers are fed raw row
// values (numbers, nulls, undefined) and rely on the runtime coercion parseFloat
// already performs. The cast keeps each call exactly as it was rather than
// introducing String(), which would change behaviour for objects and arrays.
// Same convention as src/lib/cattleNutrition.ts.

// Atomically remove zero-cow scheduled months and close later name gaps.
// Migration 182 owns authorization, stale-plan validation, locking, audit,
// collision-safe temporary renames, and the final deletes/renames.
export async function reconcileCattleScheduledBatches(
  sb: SupabaseClient,
  {
    drop = [],
    rename = [],
    teamMember = null,
  }: {drop?: ScheduledBatchPlanRow[]; rename?: ScheduledBatchPlanRow[]; teamMember?: string | null} = {},
): Promise<RpcEnvelope> {
  const plan = [
    ...drop.map((row) => ({
      id: row.id,
      expected_name: row.expectedName,
      action: 'drop',
      target_name: null,
    })),
    ...rename.map((row) => ({
      id: row.id,
      expected_name: row.expectedName,
      action: 'rename',
      target_name: row.targetName,
    })),
  ];
  if (plan.length === 0) return {ok: true, dropped: 0, renamed: 0, unchanged: true};
  const {data, error} = await sb.rpc('reconcile_cattle_scheduled_batches', {
    p_plan: plan,
    p_team_member: teamMember || null,
  });
  if (error) throw new Error('Could not reconcile planned cattle batches: ' + (error.message || String(error)));
  if (!data || data.ok !== true) {
    throw new Error('Could not reconcile planned cattle batches: ' + ((data && data.reason) || 'not_ok'));
  }
  return data;
}

export function recomputeBatchTotals(rows: CowDetailRow[]): {
  total_live_weight: number | null;
  total_hanging_weight: number | null;
} {
  const live = rows.reduce((s, r) => s + (parseFloat(r.live_weight as string) || 0), 0);
  const hang = rows.reduce((s, r) => s + (parseFloat(r.hanging_weight as string) || 0), 0);
  return {
    total_live_weight: live > 0 ? Math.round(live * 10) / 10 : null,
    total_hanging_weight: hang > 0 ? Math.round(hang * 10) / 10 : null,
  };
}

// Walk cattle.tag + old_tags the same way the webform and admin tab do when
// resolving prior-tag history. Excludes source='import' (selling-farm tags
// can collide with WCF's own numbering).
function findCowByAnyTag(cattleList: CattleRow[], tag: string | null | undefined): CattleRow | null {
  if (!tag) return null;
  const t = String(tag).trim();
  const byCurrent = cattleList.find((c) => c.tag === t);
  if (byCurrent) return byCurrent;
  return (
    cattleList.find(
      (c) => Array.isArray(c.old_tags) && c.old_tags.some((ot) => ot && ot.tag === t && ot.source !== 'import'),
    ) || null
  );
}
